/**
 * ConnectorStatisticsPage — dedicated route for connector aggregate statistics.
 *
 * Owns the redesigned layout directly instead of delegating to ConnectorStatsDashboard
 * (issue #99). Composes StatsSummaryCards, ExecutionTrendChart, RecentErrorsList,
 * ConnectorHealthTable, and TimeRangeControl with period state.
 */
import React, { useCallback, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarRange, ChevronLeft, RefreshCw } from 'lucide-react';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { Button } from '@/components/ui/button';
import { useConnectors } from '@/hooks/useConnectors';
import { useConnectorStats } from '@/hooks/useConnectorStats';
import { StatsSummaryCards } from '@/components/connectors/statistics/StatsSummaryCards';
import { ExecutionTrendChart } from '@/components/connectors/statistics/ExecutionTrendChart';
import { RecentErrorsList } from '@/components/connectors/statistics/RecentErrorsList';
import { ConnectorHealthTable } from '@/components/connectors/statistics/ConnectorHealthTable';
import { TimeRangeControl } from '@/components/connectors/statistics/TimeRangeControl';
import { CustomRangeControl } from '@/components/connectors/statistics/CustomRangeControl';
import { computeStatsTrends, computeStatsTrendsFromSummaries } from '@/components/connectors/statistics/trendDeltas';

/** The active range: a preset period, or an explicit custom window with optional comparison. */
type StatsRange =
  | { mode: 'preset'; days: number }
  | { mode: 'custom'; start: string; end: string; compareStart?: string; compareEnd?: string };

export const ConnectorStatisticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [range, setRange] = useState<StatsRange>({ mode: 'preset', days: 7 });
  const query =
    range.mode === 'preset'
      ? { periodDays: range.days }
      : {
          start: range.start,
          end: range.end,
          compareStart: range.compareStart,
          compareEnd: range.compareEnd,
        };
  const { data, isLoading, refetch } = useConnectorStats(query);
  const { data: connectorsData } = useConnectors();
  const connectors = connectorsData?.connectors ?? [];

  const handleConfigure = useCallback((connectorType: string) => {
    const c = connectors.find((c) => c.connectorType === connectorType);
    if (c) navigate(`/connectors?connector=${c.id ?? c.connectorType}&tab=overview`);
  }, [connectors, navigate]);

  const handleViewError = useCallback((execId: string, connectorType: string, connectorName: string) => {
    navigate(
      `/connectors/${connectorType}/${encodeURIComponent(connectorName)}?executionId=${encodeURIComponent(execId)}`
    );
  }, [navigate]);

  const isCustomCompare = range.mode === 'custom' && !!range.compareStart && !!range.compareEnd;
  const trends = data
    ? data.previous_summary
      ? computeStatsTrendsFromSummaries(
          data.summary,
          data.previous_summary,
          isCustomCompare ? 'vs comparison period' : undefined,
        )
      : computeStatsTrends(data.execution_trends)
    : undefined;

  const fmt = (iso?: string) => (iso ? format(parseISO(iso), 'MMM d, yyyy') : null);
  const windowLabel = data?.period_start && data?.period_end ? (
    <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
      <CalendarRange className="h-4 w-4" />
      <span className="font-medium text-foreground">
        {fmt(data.period_start)} – {fmt(data.period_end)}
      </span>
      {data.compare_start && data.compare_end && (
        <>
          <span className="rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-wide">vs</span>
          <span className="font-medium text-foreground">
            {fmt(data.compare_start)} – {fmt(data.compare_end)}
          </span>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="container mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/connectors"><ChevronLeft className="h-4 w-4 mr-1" />Back to Connectors</Link>
        </Button>
      </div>

      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Connector Statistics</h1>
          <p className="text-muted-foreground mt-1">Aggregate execution metrics across all connectors.</p>
          {data?.generated_at && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Updated {formatDistanceToNow(new Date(data.generated_at), { addSuffix: true })}
            </p>
          )}
          {windowLabel}
        </div>
        <div className="flex items-center gap-3">
          <TimeRangeControl
            value={range.mode === 'preset' ? range.days : -1}
            onChange={(days) => setRange({ mode: 'preset', days })}
          />
          <CustomRangeControl
            active={range.mode === 'custom'}
            onApply={(r) => setRange({ mode: 'custom', ...r })}
          />
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />Refresh
          </Button>
        </div>
      </div>

      <StatsSummaryCards stats={data?.summary ?? null} isLoading={isLoading} trends={trends} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <ExecutionTrendChart data={data?.execution_trends ?? []} periodDays={data?.period_days ?? (range.mode === 'preset' ? range.days : 7)} isLoading={isLoading} />
        </div>
        <div className="lg:col-span-1">
          <RecentErrorsList errors={data?.recent_errors ?? []} isLoading={isLoading} onViewDetails={handleViewError} />
        </div>
      </div>

      <ConnectorHealthTable connectors={data?.connector_health ?? []} isLoading={isLoading} onConfigure={handleConfigure} />
    </div>
  );
};
